import { OrderAction, OrderActionType } from '../actions/order';
import { DeliveryMethodsByCountry, ShippingSettings } from '../models/event';
import { WithOrderNamespace } from '../state/order';
import { getDeliveryMethodsAfterCheckingShippingBackup } from '../utils/deliveryMethod';

const getAttendeesWithTicketDeliveryMethod = (
    attendees: Array<{
        ticketClass?: {
            deliveryMethods: DeliveryMethodsByCountry;
        };
    }>,
    shippingSettings: ShippingSettings,
) =>
    attendees.map(({ ticketClass = {}, ...otherProps }) => {
        const { deliveryMethods = {}, ...otherTicketClassProps } = ticketClass;

        return {
            ...otherProps,
            ticketClass: {
                ...otherTicketClassProps,
                deliveryMethods: getDeliveryMethodsAfterCheckingShippingBackup(
                    deliveryMethods,
                    shippingSettings,
                ),
            },
        };
    });

export const orderReducer = (
    state: WithOrderNamespace['order'] = {},
    action: OrderAction,
): WithOrderNamespace['order'] => {
    switch (action.type) {
        case OrderActionType.resetOrder:
            return {};

        case OrderActionType.updateOrder:
            // When the order has costsToCheckout we need to use that instead of orderCosts but using
            // the same name to avoid further changes on all the Checkout codebase
            const { costsToCheckout, ...payload } = action.payload;

            const transformedPayload = costsToCheckout
                ? {
                      ...payload,
                      costs: costsToCheckout,
                  }
                : action.payload;

            return {
                ...state,
                ...transformedPayload,
            };

        case OrderActionType.updateOrderAttendees:
            return {
                ...state,
                attendees: action.payload,
            };

        case OrderActionType.updateOrderEmail:
            return {
                ...state,
                email: action.payload.email,
            };

        case OrderActionType.updateOrderStatus:
            return {
                ...state,
                status: action.payload,
            };

        case OrderActionType.updateRedirectInstructions:
            return {
                ...state,
                redirectInstructions: action.payload,
            };

        case OrderActionType.updateAvailablePaymentMethods:
            return {
                ...state,
                availablePaymentMethods: action.payload.paymentOptions,
            };

        case OrderActionType.updateBraintreeClientToken:
            return action.payload
                ? {
                      ...state,
                      braintreeClientToken: action.payload,
                  }
                : state;

        case OrderActionType.saveWaitlistEntry:
            return {
                ...state,
                waitlistEntry: action.payload,
            };

        case OrderActionType.setOrderUpdating:
            return {
                ...state,
                isUpdating: action.payload,
            };

        case OrderActionType.setCanRetrySubmit:
            return {
                ...state,
                canRetrySubmit: action.payload,
            };

        case OrderActionType.setOrderInstallments:
            return {
                ...state,
                displayInstallments: action.payload,
            };

        case OrderActionType.removeOrderInstallments:
            return {
                ...state,
                displayInstallments: undefined,
            };

        case OrderActionType.saveEventShippingSettings:
            return {
                ...state,
                attendees: getAttendeesWithTicketDeliveryMethod(
                    'attendees' in state ? state.attendees : [],
                    action.payload.shippingSettings,
                ),
            };

        case OrderActionType.taxPoliciesLoadComplete:
            return {
                ...state,
                taxIdName: action.payload,
            };

        case OrderActionType.updateTaxExemptionMessage:
            return {
                ...state,
                taxExemptionData: action.payload,
            };

        case OrderActionType.setHasVirtualIncentivesErrors:
            return {
                ...state,
                hasVirtualIncentivesErrors: action.payload,
            };

        case OrderActionType.setHasRetryPaymentError:
            return {
                ...state,
                hasRetryPaymentError: action.payload,
            };

        case OrderActionType.setHasExternalPaymentFirstCallCompleted:
            return {
                ...state,
                hasExternalPaymentFirstCallCompleted: action.payload,
            };

        case OrderActionType.setRedirectedPaymentTimeoutId:
            return {
                ...state,
                redirectedPaymentTimeoutId: action.payload,
            };
        case OrderActionType.clearRedirectedPaymentTimeoutId:
            return {
                ...state,
                redirectedPaymentTimeoutId: undefined,
            };

        default:
            return state;
    }
};
